import "./multi-select-field.scss";
import Label from "../Label/Label";
import InfoIcon from "../InfoIcon/InfoIcon";
import HintLabel from "../HintLabel/HintLabel";
import React, { useEffect, useRef, useState } from "react";
import { isValidJSON, isValidJSONString } from "../../utilities";
import InputDescription from "../InputDescription/InputDescription";
import ErrorLabelWithValue from "../ErrorLabelWithValue/ErrorLabelWithValue";
import PendingFieldConfirmButton from "../PendingFieldConfirmButton/PendingFieldConfirmButton";

const MultiSelectField = (props) => {
  const {
    id,
    label,
    className,
    placeholder,
    choices,
    hintText,
    description,
    value,
    disabled,
    error,
    pending,
    isRequired,
    validations,
    onChange,
    onBlur,
    dataType: format,
  } = props;

  const selections =
    value != null && isValidJSONString(value) ? JSON.parse(value) : [];

  const [show, setShow] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const multiSelectRef = useRef(null);
  const multiSelectFieldRef = useRef(null);
  const multiSelectOptionsRef = useRef(null);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleClickOutside = (event) => {
    if (!multiSelectRef?.current?.contains(event.target)) {
      setShowDropdown(false);
    } else {
      multiSelectFieldRef?.current?.focus();
    }
  };

  const handleToggle = () => setShow(!show);

  const handleToggleDropdown = (event) => {
    if (!showDropdown) {
      multiSelectFieldRef?.current?.focus();
    }
    if (multiSelectOptionsRef?.current?.contains(event.target)) {
      return;
    }
    setShowDropdown(!showDropdown);
  };

  const handleSelectOption = (value) => {
    let newSelections = [...selections];
    if (newSelections.includes(value)) {
      newSelections = newSelections.filter((item) => item !== value);
    } else {
      newSelections.push(value);
    }
    if (isValidJSON(newSelections)) {
      const payload = {
        target: { name: id, value: JSON.stringify(newSelections) },
      };
      onChange(payload);
      onBlur(payload);
    }
  };

  const getValue = (value) =>
    format === "int" ? parseInt(value) : String(value);

  const selectionsText =
    value != null && isValidJSONString(value)
      ? choices.entries
          .filter(({ value: rawValue }) =>
            selections.includes(getValue(rawValue))
          )
          .map(({ label }) => label)
          .join(", ")
      : "";

  return (
    <div className="input-field dcrform-multi-select-field">
      <Label className={pending ? "pending-label" : ""}>{label}</Label>
      <div className={`input-field__wrapper ${className}`}>
        <div className="input-field__inner-wrapper">
          <div className="input-field__confirm-wrapper">
            <div
              ref={multiSelectRef}
              onClick={handleToggleDropdown}
              className="multi-select-container"
            >
              <div className="multi-select-field">
                <input
                  readOnly
                  name={id}
                  disabled={disabled}
                  value={selectionsText}
                  ref={multiSelectFieldRef}
                  placeholder={placeholder}
                  className={`input-field__input ${
                    isRequired ? "error-border" : ""
                  }`}
                />
                <span className="icon fa fa-chevron-down fa-xs" />
              </div>
              {showDropdown && (
                <ul className="options" ref={multiSelectOptionsRef}>
                  <li className="option disabled">{placeholder}</li>
                  {choices.entries.map(({ label, value: rawValue }) => {
                    const value = getValue(rawValue);

                    return (
                      <li
                        key={value}
                        className="option"
                        onClick={() => handleSelectOption(value)}
                      >
                        <input
                          readOnly
                          type="checkbox"
                          className="selection-box"
                          checked={selections.includes(value)}
                        />
                        {label}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {pending && value && !showDropdown && (
              <PendingFieldConfirmButton onClick={() => onBlur(value)} />
            )}
          </div>
          {description && <InfoIcon onClick={handleToggle} active={show} />}
        </div>
        <ErrorLabelWithValue
          error={error}
          isRequired={isRequired}
          validations={validations}
        />
        {hintText && <HintLabel>{hintText}</HintLabel>}
        {description && show && (
          <InputDescription>{description}</InputDescription>
        )}
      </div>
    </div>
  );
};

export default MultiSelectField;
